import { useMemo } from "react";

/**
 * Aggregate raw workspace rows into unique per-`id_client` select options,
 * merging markets and core_brands across duplicate rows. Deep-copies the input
 * first so Redux state is never mutated. Returns `[{ value, label, workspace }]`.
 *
 * Framework-light (React `useMemo` only, no store coupling): the caller passes
 * its own `allWorkspaces`, so both apps can reuse it.
 *
 * @param {any[]} allWorkspaces
 * @returns {{ value: string, label: string, workspace: object }[]}
 */
export function useWorkspaceMarketOptions(allWorkspaces) {
  return useMemo(() => {
    const workspacesCopy = JSON.parse(JSON.stringify(allWorkspaces ?? []));

    const workspaceMap = workspacesCopy.reduce((acc, workspace) => {
      const clientId = workspace.id_client;

      if (!acc[clientId]) {
        // First time seeing this client, create the entry
        acc[clientId] = {
          id_client: clientId,
          region: workspace.region,
          mapped_id_client: workspace.mapped_id_client,
          markets: workspace.markets ? [...workspace.markets] : [],
          core_brands: workspace.core_brands ? [...workspace.core_brands] : [],
          // Keep other properties from the first occurrence
          market_penetration: workspace.market_penetration,
          market_penetration_branded: workspace.market_penetration_branded,
          core_availability: workspace.core_availability,
          combo_presence: workspace.combo_presence,
          fixed_combo_presence: workspace.fixed_combo_presence,
          option_combo_presence: workspace.option_combo_presence,
          name_accuracy: workspace.name_accuracy,
          image_presence: workspace.image_presence,
        };
      } else {
        // Client already exists, merge the markets
        if (
          workspace.markets &&
          workspace.markets[0] &&
          !acc[clientId].markets.includes(workspace.markets[0])
        ) {
          acc[clientId].markets.push(workspace.markets[0]);
        }

        // Optionally merge core_brands if they're different
        if (workspace.core_brands && workspace.core_brands.length > 0) {
          const existingBrands = acc[clientId].core_brands.map((b) => b.name);
          workspace.core_brands.forEach((brand) => {
            if (!existingBrands.includes(brand.name)) {
              acc[clientId].core_brands.push(brand);
            }
          });
        }
      }

      return acc;
    }, {});

    return Object.values(workspaceMap).map((workspace) => ({
      value: workspace.id_client,
      label: `${workspace.id_client} (${workspace.markets.length} markets)`,
      workspace,
    }));
  }, [allWorkspaces]);
}
